import { NavItemListType, NavItemType } from "types";

/**
 * The caption of the group the selected nav item sits in — "Analytics" for
 * a page under the SocialPro group, "Manage" for Work Packages. What the top
 * bar's trailing crumb reads.
 *
 * Searches nested children too: a page under an expandable row (Participation
 * under SocialPro) belongs to the row's group, not to the row. Returns "" when
 * nothing matches — a page the sidebar does not list, or an untitled group —
 * and the bar then falls back to the item's own title.
 */
export const sectionOf = (
  menu: NavItemListType | undefined,
  selectedItem: string
): string => {
  if (!menu || !selectedItem) return "";
  for (const group of menu.items) {
    if (find(group.children ?? [], selectedItem)) {
      return typeof group.title === "string" ? group.title : "";
    }
  }
  return "";
};

/** The selected item's own title, wherever it sits in the tree. */
export const titleOf = (
  menu: NavItemListType | undefined,
  selectedItem: string
): string => {
  if (!menu || !selectedItem) return "";
  const item = find(menu.items, selectedItem);
  return item && typeof item.title === "string" ? item.title : "";
};

/**
 * The trailing crumb for a selected nav item: its group's caption, falling back
 * to the item's own title for a page the sidebar lists outside any group (or
 * does not list at all — Settings reads as itself).
 *
 * Both sidebar wrappers publish this, and both used to spell the fallback out.
 * `sectionOf` keeps its own contract — "" when nothing matches — because the
 * bar is not its only reader.
 */
export const crumbLabelFor = (
  menu: NavItemListType | undefined,
  selectedItem: string
): string => sectionOf(menu, selectedItem) || titleOf(menu, selectedItem);

const find = (items: NavItemType[], id: string): NavItemType | undefined => {
  for (const item of items) {
    if (item.id === id) return item;
    const nested = find(item.children ?? [], id);
    if (nested) return nested;
  }
  return undefined;
};
